import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addGiftLinks, quoteGiftLinkAdditions } from "@/api/giftLinks";
import { useCampaignEndpoints } from "@/context/CampaignEndpointsContext";
import type { AddGiftLinksBody, QuoteGiftLinksBody } from "@/interfaces/giftLinks";
import { CREDIT_QUERY_KEYS } from "./useCampaignContacts";

/**
 * Price of adding `body.count` gift links with the chosen postpaid `depositMode` (absent means the
 * server default, card). Mirrors `useRecipientAdditionsQuote`: keyed under `["campaignContacts", id]`
 * so a contacts-list invalidation re-quotes it, never kept past its last observer (`gcTime: 0`), and
 * the previous quote stays on screen while a new deposit mode loads (`placeholderData`). Callers must
 * check `isPlaceholderData` / `isFetching` and not act on that stale price.
 */
export function useGiftLinkAdditionsQuote(
  campaignId: string,
  body: QuoteGiftLinksBody,
  enabled: boolean,
) {
  const path = useCampaignEndpoints().campaign(campaignId);
  return useQuery({
    queryKey: [
      "campaignContacts",
      campaignId,
      "giftLinksQuote",
      path,
      body.count,
      body.depositMode ?? "card",
    ],
    queryFn: () => quoteGiftLinkAdditions(campaignId, body, path),
    enabled: enabled && !!campaignId && body.count > 0,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

/**
 * Adds gift links to the campaign. Settles by invalidating the contacts list (a 422 means the quote
 * moved, so it must re-quote), `["campaigns"]` (the card's link count), the budget history, and the
 * credit caches, since an add can buy credits or draw on wallets.
 */
export function useAddGiftLinks(campaignId: string) {
  const queryClient = useQueryClient();
  const endpoints = useCampaignEndpoints();
  const path = endpoints.campaign(campaignId);

  return useMutation({
    mutationFn: (body: AddGiftLinksBody) => addGiftLinks(campaignId, body, path),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["campaignContacts", campaignId] });
      queryClient.invalidateQueries({ queryKey: endpoints.listKey });
      queryClient.invalidateQueries({ queryKey: ["campaignBudgetAmendments", campaignId] });
      for (const queryKey of CREDIT_QUERY_KEYS) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
